'use client';

import { useEffect, useState } from 'react';
import { usePathname } from 'next/navigation';
import { isCapacitor } from '@/lib/platformUtils';
import { patchFetchForCapacitor } from '@/lib/capacitorFetchPatch';
import { lifecycleManager } from '@/lib/capacitorLifecycleManager';
import { capacitorOTAUpdateService } from '@/lib/capacitorOTAUpdateService';
import { cloudflareRecoveryService } from '@/lib/cloudflareRecoveryService';
import { writePersistedLastVisitedModule } from '@/lib/offline/lastVisitedModuleStore';
import { autoCleanupLegacyStorage } from '@/lib/offline/storageCleanup';
import { detectAndClearStaleOfflineDb } from '@/lib/offline/offlineDbMaintenance';
import masterSync from '@/lib/masterSync';
import { setupWebSocketLifecycle } from '@/lib/websocket/lifecycle';
import { isIntentionalDisconnectError, wsClient } from '@/lib/websocket';
import { useBuildVersionChecker } from '@/hooks/useBuildVersionChecker';
import CookieConsent from './CookieConsent';
import PublicAIChatbot from './PublicAIChatbot';

interface ClientLayoutProps {
  gaMeasurementId: string;
}

type PendingCallWindow = Window & {
  __pendingCallAnswer?: unknown;
  TotalAccessNative?: {
    getPendingCall?: () => unknown;
  };
};

type ConsoleArgument = string | number | boolean | null | undefined | { message?: string; toString?: () => string };

/**
 * ClientLayout handles platform-specific components (cookie consent, chatbot)
 * These components are only shown in web browser, not in Capacitor app
 */
// ----------------------------------------------------------------------------
// Module-context breadcrumb
// Written on every route change to sessionStorage + TotalAccessOfflineDB so
// /client-zone/diagnostics-auth can recover the user's last-visited module even
// when document.referrer is empty (e.g. Capacitor, Tauri, or same-origin
// navigations stripped by CSP). Mirrors the regexes in
// src/lib/diagnostics/probes.ts → detectModuleContext().
// ----------------------------------------------------------------------------
const MODULE_CONTEXT_BREADCRUMB_KEY = 'lastModuleContext';

function deriveModuleFromPath(pathname: string): string | null {
  if (!pathname) return null;
  const moduleMatch = pathname.match(/^\/client-zone\/modules\/([^/?#]+)/);
  if (moduleMatch) return moduleMatch[1];
  if (pathname.startsWith('/client-zone/admin-hub')) return 'admin-hub';
  if (pathname.startsWith('/client-zone/portal')) return 'customer-portal';
  return null;
}

export default function ClientLayout({ gaMeasurementId }: ClientLayoutProps) {
  const pathname = usePathname();
  const [isCapacitorApp, setIsCapacitorApp] = useState(false);
  const [mounted, setMounted] = useState(false);

  // Proactively detect new server deployments and reload before chunk
  // errors occur. Only active on Capacitor (mobile app). Polls
  // /api/v1/build-info every 3 minutes and on app resume.
  useBuildVersionChecker();

  // Persist the last-visited module so the diagnostic collector can attribute
  // a snapshot to the right module without relying on HTTP referrer.
  useEffect(() => {
    if (typeof window === 'undefined' || !pathname) return;
    // Don't overwrite when the user is already on the diagnostics page,
    // otherwise we'd lose the previous module context every time the page
    // re-renders.
    if (pathname.startsWith('/client-zone/diagnostics-auth')) return;
    const moduleKey = deriveModuleFromPath(pathname);
    if (!moduleKey) return;
    try {
      sessionStorage.setItem(
        MODULE_CONTEXT_BREADCRUMB_KEY,
        JSON.stringify({
          module: moduleKey,
          route: pathname,
          recorded_at: new Date().toISOString(),
        }),
      );
    } catch {
      /* sessionStorage may be disabled in private mode — non-fatal */
    }

    void writePersistedLastVisitedModule(pathname, moduleKey).catch(() => undefined);
  }, [pathname]);

  useEffect(() => {
    setMounted(true);
    setIsCapacitorApp(isCapacitor());

    // Patch fetch() for Capacitor to automatically convert relative URLs to absolute
    patchFetchForCapacitor();

    // Clean up legacy IndexedDB/localStorage from older versions.
    // This must run before offline storage access so the app only uses TotalAccessOfflineDB.
    void autoCleanupLegacyStorage();

    // Detect stale/corrupted offline DB (e.g. schema mismatch from app update)
    // and automatically clear all IndexedDB databases if the DB fails to open.
    // This prevents the app from hanging on stale offline data.
    void detectAndClearStaleOfflineDb();

    // Proactive cold-start cache validation — detects and cleans stale cache
    // data (old navigation snapshots, stuck pending records, dead-letter queue
    // entries) that can cause the dashboard to hang on loading. Runs at most
    // once per hour to avoid overhead.
    void masterSync.runColdStartCacheValidation().catch((error) => {
      console.warn('[ClientLayout] Cold-start cache validation failed:', error);
    });

    // Initialize Capacitor lifecycle manager (handles app state, network changes)
    if (isCapacitor()) {
      lifecycleManager.initialize().then(() => {
        // Start OTA update checks with lifecycle integration
        capacitorOTAUpdateService.startLifecycleChecks();
        // Start Cloudflare Error 1033 recovery monitoring
        cloudflareRecoveryService.start();
      }).catch(err => {
        console.warn('[ClientLayout] Lifecycle manager init failed:', err);
      });
    }

    // Bootstrap WebSocket connection + lifecycle management
    // setupWebSocketLifecycle() bridges platform events (Capacitor/Tauri/Web) to wsClient
    // On Capacitor: subscribes to lifecycleManager (no duplicate native listeners)
    // On Tauri: uses window focus API
    // On Web: uses visibility/online/offline events
    const cleanupWsLifecycle = setupWebSocketLifecycle();

    const hasPendingColdStartCall = (): boolean => {
      if (typeof window === 'undefined') {
        return false;
      }

      try {
        const url = new URL(window.location.href);
        if (url.searchParams.get('action') === 'ANSWER_CALL' || url.searchParams.get('call_id')) {
          return true;
        }

        const pendingCallWindow = window as PendingCallWindow;

        if (pendingCallWindow.__pendingCallAnswer) {
          return true;
        }

        if (sessionStorage.getItem('pending_call_answer')) {
          return true;
        }

        if (
          localStorage.getItem('totalaccess_pending_call')
          || localStorage.getItem('coldstart_pending_call')
        ) {
          return true;
        }

        const { TotalAccessNative } = pendingCallWindow;
        if (TotalAccessNative && typeof TotalAccessNative.getPendingCall === 'function') {
          return !!TotalAccessNative.getPendingCall();
        }
      } catch (error) {
        console.warn('[ClientLayout] Pending call detection failed during WebSocket bootstrap:', error);
      }

      return false;
    };

    // Only connect if auth tokens exist — avoids noisy errors on public/login pages.
    // wsClient.connect() will throw "No access token" on unauthenticated routes.
    // On authenticated pages, authService loads tokens from localStorage synchronously.
    const tryConnect = async () => {
      try {
        const { authService } = await import('@/lib/authService');
        const currentPathname = typeof window !== 'undefined' ? window.location.pathname : '';
        // On Capacitor, wait for async storage to be ready
        if (isCapacitor() && authService.waitForStorageInit) {
          if (authService.hasSyncHydratedSession()) {
            void authService.waitForStorageInit();
          } else {
            await authService.waitForStorageInit();
          }
        }

        if (
          isCapacitor()
          && currentPathname.startsWith('/client-zone')
          && authService.hasSyncHydratedSession?.()
        ) {
          // Avoid eager refresh during Capacitor cold start.
          // localStorage may still contain an older hydrated session while native Preferences
          // continues reconciling in the background, and forcing `/auth/refresh` here can hit
          // a revoked refresh token from the older session. That creates 401/reconnect churn
          // even though the shell is already interactive and the final restored session is valid.
          if (hasPendingColdStartCall()) {
          } else if (wsClient.getStatus() !== 'disconnected') {
          }
        }

        const tokens = authService.getTokens();
        if (tokens?.access_token) {
          await wsClient.connect();
        }
      } catch (err: unknown) {
        if (!isIntentionalDisconnectError(err)) {
          const message = err instanceof Error ? err.message : String(err);
          console.warn('[ClientLayout] WebSocket connect deferred:', message);
        }
      }
    };
    tryConnect();

    // Global error handler to catch and suppress offline errors
    // This prevents unhandled promise rejections from crashing the app
    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      const error = event.reason;

      // Suppress offline errors - they're expected when network is unavailable
      if (error?.message?.includes('offline') ||
        error?.message?.includes('Network unavailable') ||
        error?.message?.includes('ERR_INTERNET_DISCONNECTED') ||
        error?.message?.includes('Failed to fetch onboarding') ||
        error?.isOfflineError) {
        event.preventDefault(); // Prevent the error from being logged
        return;
      }

      // Suppress Next.js dev/HMR errors
      if ((error?.message?.includes('_next') && error?.message?.toLowerCase?.().includes('hmr')) ||
        error?.message?.includes('__nextjs_') ||
        error?.isHmrError) {
        event.preventDefault(); // Prevent the error from being logged
        return;
      }

      if (
        typeof error?.message === 'string' &&
        (error.message.includes('WebSocket is already in CLOSING') ||
          error.message.includes('CLOSING or CLOSED state'))
      ) {
        event.preventDefault();
        return;
      }

      // Let other errors through for proper debugging
    };

    // Global error event handler for non-promise errors
    const handleError = (event: ErrorEvent) => {
      const error = event.error;

      // Suppress offline errors
      if (error?.message?.includes('offline') ||
        error?.message?.includes('Network unavailable') ||
        error?.message?.includes('ERR_INTERNET_DISCONNECTED') ||
        error?.message?.includes('Failed to fetch onboarding') ||
        error?.isOfflineError) {
        event.preventDefault();
        return;
      }

      // Suppress Next.js dev/HMR errors
      if ((error?.message?.includes('_next') && error?.message?.toLowerCase?.().includes('hmr')) ||
        error?.message?.includes('__nextjs_') ||
        error?.isHmrError) {
        event.preventDefault();
        return;
      }

      if (
        typeof error?.message === 'string' &&
        (error.message.includes('WebSocket is already in CLOSING') ||
          error.message.includes('CLOSING or CLOSED state'))
      ) {
        event.preventDefault();
        return;
      }
    };

    // Suppress console.error for WebSocket HMR errors and offline errors
    const originalConsoleLog = console.log;
    const originalConsoleError = console.error;
    const originalConsoleWarn = console.warn;

    console.log = function (...args: ConsoleArgument[]) {
      const fullMessage = args
        .map((a) => {
          if (typeof a === 'string') return a;
          if (a && typeof a === 'object' && typeof a.message === 'string') return a.message;
          try {
            return JSON.stringify(a);
          } catch {
            return a?.toString?.() || '';
          }
        })
        .join(' ');

      if (
        fullMessage.includes('[Geolocation] Watch update received:') ||
        fullMessage.includes('[Geolocation] Browser watch update received:') ||
        fullMessage.includes('[Geolocation] Permission status:') ||
        fullMessage.includes('[SmartClock] 🔄 Periodic GPS refresh for geofence accuracy...')
      ) {
        return;
      }

      originalConsoleLog.apply(console, args);
    };

    console.error = function (...args: ConsoleArgument[]) {
      const message = args[0]?.toString?.() || '';
      const fullMessage = args.map(a => a?.toString?.() || '').join(' ');

      // Suppress WebSocket HMR connection errors and all offline-related errors
      if (fullMessage.includes('WebSocket connection') ||
        fullMessage.includes('WebSocket is already in CLOSING') ||
        fullMessage.includes('CLOSING or CLOSED state') ||
        (fullMessage.includes('_next') && fullMessage.toLowerCase().includes('hmr')) ||
        fullMessage.includes('Created TensorFlow Lite XNNPACK delegate for CPU') ||
        fullMessage.includes('TfLiteXNNPACKDelegate') ||
        fullMessage.includes('ERR_INTERNET_DISCONNECTED') ||
        fullMessage.includes('Fetch Patch') ||
        fullMessage.includes('FaceAPI') ||
        fullMessage.includes('Missing models') ||
        fullMessage.includes('Failed to fetch onboarding') ||
        fullMessage.includes('Failed to load mascot') ||
        fullMessage.includes('Failed to fetch trial') ||
        fullMessage.includes('Network unavailable') ||
        fullMessage.includes('device is offline') ||
        fullMessage.includes('Error loading branding') ||
        fullMessage.includes('Failed to fetch fresh user') ||
        fullMessage.includes('Failed to fetch public branding') ||
        fullMessage.includes('Updating a style property during rerender') ||
        fullMessage.includes('animationDelay') ||
        fullMessage.includes('Could not fetch a valid release JSON') ||
        fullMessage.includes('Failed to check for updates') ||
        fullMessage.includes('Tried to add a track for a participant') ||
        fullMessage.includes('publishing rejected as engine not connected') ||
        fullMessage.includes('[GlobalMessageNotifier] WebSocket error') ||
        fullMessage.includes('[WS] Reconnect failed') ||
        fullMessage.includes('Connection closed before authentication completed') ||
        fullMessage.includes('supervisor-stats') ||
        fullMessage.includes('operations/user-role') ||
        fullMessage.includes('Failed to fetch RSC payload') ||
        fullMessage.includes('Falling back to browser navigation')) {
        // Silently ignore - these are expected when offline or in development
        return;
      }

      // Call original console.error for all other errors
      // Detect "Failed to find Server Action" — stale cached JS after deploy
      if (fullMessage.includes('Failed to find Server Action')) {
        if (!sessionStorage.getItem('_ta_sa_reloaded')) {
          sessionStorage.setItem('_ta_sa_reloaded', '1');
          console.warn('[ClientLayout] Stale server action detected, clearing caches and reloading...');
          // Clear SW caches then reload — prevents stale Server Action IDs
          // from persisting after a deploy
          const reload = () => window.location.reload();
          if (typeof caches !== 'undefined') {
            Promise.all([
              caches.delete('navigation-pages-v2'),
              caches.delete('nextjs-chunks-v2'),
              caches.delete('navigation-pages-v1'),
              caches.delete('nextjs-chunks-v1'),
            ]).then(reload).catch(reload);
          } else {
            reload();
          }
        }
        return;
      }
      originalConsoleError.apply(console, args);
    };

    console.warn = function (...args: ConsoleArgument[]) {
      const fullMessage = args
        .map((a) => {
          if (typeof a === 'string') return a;
          if (a && typeof a === 'object' && typeof a.message === 'string') return a.message;
          try {
            return JSON.stringify(a);
          } catch {
            return a?.toString?.() || '';
          }
        })
        .join(' ');

      // Suppress offline-related warnings and style warnings
      if (fullMessage.includes('Failed to send events') ||
        fullMessage.includes('WebSocket is already in CLOSING') ||
        fullMessage.includes('CLOSING or CLOSED state') ||
        fullMessage.includes('OpenGL error checking is disabled') ||
        fullMessage.includes('Feedback manager requires a model with a single signature inference') ||
        fullMessage.includes('inference_feedback_manager.cc:121') ||
        fullMessage.includes('gl_context.cc:1060') ||
        fullMessage.includes('OS-PLUG-GLOC-0007') ||
        fullMessage.includes('Location services are not enabled.') ||
        fullMessage.includes('Item with given key does not exist') ||
        fullMessage.includes('Service worker file not accessible') ||
        fullMessage.includes('Updating a style property during rerender') ||
        fullMessage.includes('animationDelay') ||
        fullMessage.includes('has either width or height modified, but not the other') ||
        fullMessage.includes('outdated JSX transform') ||
        fullMessage.includes('new-jsx-transform') ||
        fullMessage.includes('[WS] Transport error event received') ||
        fullMessage.includes('[WS] Connection closed before transport opened') ||
        fullMessage.includes('[WS] Reconnect failed') ||
        fullMessage.includes('[WS Lifecycle] Failed to set up Tauri lifecycle') ||
        fullMessage.includes('[NAVIGATION] Clearing stale offline navigation cache') ||
        (fullMessage.includes("Dexie.delete('") && fullMessage.includes("') was blocked")) ||
        (fullMessage.includes('[storageCleanup] Database') && fullMessage.includes('deletion blocked'))) {
        return;
      }

      // Call original console.warn for all other warnings
      originalConsoleWarn.apply(console, args);
    };

    window.addEventListener('unhandledrejection', handleUnhandledRejection);
    window.addEventListener('error', handleError);

    return () => {
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
      window.removeEventListener('error', handleError);

      // Restore original console methods
      console.log = originalConsoleLog;
      console.error = originalConsoleError;
      console.warn = originalConsoleWarn;

      // Stop OTA lifecycle checks on cleanup
      if (isCapacitor()) {
        capacitorOTAUpdateService.stopLifecycleChecks();
        cloudflareRecoveryService.stop();
      }

      // Tear down WebSocket lifecycle bridge
      cleanupWsLifecycle();
    };
  }, []);

  // Don't render conditional elements until mounted (prevents hydration mismatch)
  if (!mounted) {
    return null;
  }

  // Public routes where chatbot should be hidden
  const isPublicRoute = pathname?.startsWith('/secure-link/') || pathname?.startsWith('/ref-documents/') || pathname?.startsWith('/sign/') || pathname?.startsWith('/invitation/') || pathname?.startsWith('/scan/') || pathname?.startsWith('/enroll/') || pathname?.startsWith('/biometric/') || pathname?.startsWith('/document-upload/') || pathname?.startsWith('/vehicle-inspection/') || pathname?.startsWith('/field-action/') || pathname?.startsWith('/forms/') || pathname?.startsWith('/vote/') || pathname?.startsWith('/visit/') || pathname?.startsWith('/fleet/') || pathname?.startsWith('/careers/') || pathname?.startsWith('/shared-documents/') || pathname?.startsWith('/cloud-storage/share/') || pathname?.startsWith('/wi/') || pathname?.startsWith('/connect-adapter/') || pathname?.startsWith('/desktop/') || pathname?.startsWith('/verify-email') || pathname?.startsWith('/kiosk') || pathname?.startsWith('/deeplink') || pathname?.startsWith('/auth-blocked') || pathname?.startsWith('/maintenance') || pathname?.startsWith('/rate-limited')
    // Public standalone/flow pages that don't match a whole prefix:
    // /share (share receiver), /support/verify (guest email verification),
    // /oauth/consent (consent screen), /worktracker + /productivity-agent
    // (compact Tauri standalone windows), /playstore + /appgallery (store redirects)
    || pathname === '/share' || pathname === '/support/verify' || pathname === '/oauth/consent' || pathname === '/worktracker' || pathname === '/productivity-agent' || pathname === '/playstore' || pathname === '/appgallery';

  // Hide chatbot on support page and call/meeting pages
  const isSupportPage = pathname === '/support' || pathname?.startsWith('/support/');
  const isCallOrMeetingPage = pathname?.startsWith('/call/') || pathname?.startsWith('/meeting/');

  return (
    <>
      {/* Only show cookie consent and chatbot in web browser, not in Capacitor or public routes or call/meeting pages */}
      {!isCapacitorApp && !isPublicRoute && !isCallOrMeetingPage && <CookieConsent />}
      {!isCapacitorApp && !isPublicRoute && !isSupportPage && !isCallOrMeetingPage && <PublicAIChatbot />}
    </>
  );
}
